import type {
  AcademicOverviewInsights,
  AttendanceToday,
  OverviewAvailability,
  OverviewDomain,
} from '../types';

/**
 * Normalises the wire payload into the executive contract the components read.
 *
 * EduCore's director rebuild and this console were written against one agreed
 * contract, but the server ships some blocks under sibling names: finance flat
 * rather than under `year`, today's register inside `attendanceDetail`, scheme
 * donuts as `academics.byScheme`, the boarding/day split as
 * `population.residency`, and the admissions funnel nested in the legacy
 * `admissions` block. Reading those shapes raw crashed the Home page on
 * `finance.year` — so every alias is folded here, in one place, and the
 * components stay written against the single contract. Fields already in the
 * expected shape pass through untouched, which keeps this a no-op for a server
 * that says exactly what the types say.
 */

interface WireAttendanceDetail {
  availability?: 'DATA' | 'NO_DATA';
  today?: AttendanceToday;
  last7?: { recorded: number; rate: number | null };
  last30?: { recorded: number; rate: number | null };
  series?: Array<{ day: string; recorded?: number; rate: number | null }>;
  belowThreshold?: number;
  threshold?: number;
}

interface WireSchemeBlock {
  schemeId?: string;
  schemeName?: string;
  assessed?: number;
  average?: number | null;
  distribution?: Array<{ grade: string; count: number }>;
}

type WireOverview = AcademicOverviewInsights & {
  attendanceDetail?: WireAttendanceDetail;
  academics?: AcademicOverviewInsights['academics'] & {
    availability?: 'DATA' | 'NO_DATA' | 'NO_ACTIVE_YEAR';
    byScheme?: WireSchemeBlock[];
    deciles?: Array<{ bucket: string; count: number }>;
  };
  finance?: Partial<NonNullable<AcademicOverviewInsights['finance']>> & {
    entitled?: boolean;
    availability?: 'DATA' | 'NO_DATA' | 'NO_ACTIVE_YEAR' | 'NOT_ENTITLED';
    billed?: number;
    collected?: number;
    outstanding?: number;
    collectionRate?: number | null;
  };
  population?: AcademicOverviewInsights['population'] & {
    residency?: { boarding: number; day: number };
  };
  admissions?: AcademicOverviewInsights['admissions'] & {
    funnel?: {
      cycle?: { label?: string } | null;
      applications: number;
      underReview: number;
      assessed: number;
      offered: number;
      accepted: number;
      enrolled: number;
      pendingOver7Days?: number;
      placementScheduled?: number;
    };
  };
  atRisk?: { count: number; threshold?: number };
};

const availabilityOf = (
  raw: string | undefined,
): OverviewAvailability | undefined => {
  switch (raw) {
    case 'DATA':
      return 'DATA';
    case 'NO_DATA':
      return 'NO_ACTIVITY';
    case 'NO_ACTIVE_YEAR':
      return 'NOT_CONFIGURED';
    default:
      return undefined;
  }
};

export function adaptExecutive(data: AcademicOverviewInsights): AcademicOverviewInsights {
  const raw = data as WireOverview;
  const out: WireOverview = { ...raw };

  // Finance: flat wire figures fold under `year`; a not-entitled block is dropped
  // entirely so no money region renders at all.
  const finance = raw.finance;
  if (finance) {
    if (finance.entitled === false || finance.availability === 'NOT_ENTITLED') {
      delete out.finance;
    } else if (!finance.year && typeof finance.billed === 'number') {
      out.finance = {
        year: {
          billed: finance.billed,
          collected: finance.collected ?? 0,
          outstanding: finance.outstanding ?? 0,
          collectionRate: finance.collectionRate ?? null,
        },
        thisMonth: finance.thisMonth ?? { collected: 0, payments: 0 },
        ...(finance.statusMix ? { statusMix: finance.statusMix } : {}),
        ...(finance.currency !== undefined ? { currency: finance.currency } : {}),
      };
    } else if (!finance.year) {
      // Present but unreadable either way — better no section than a crash.
      delete out.finance;
    }
  }

  // Attendance: today's register, the 7-day mean and the risk count arrive in
  // `attendanceDetail`; the legacy summary keeps its own series.
  const detail = raw.attendanceDetail;
  if (detail && raw.attendance) {
    out.attendance = {
      ...raw.attendance,
      ...(raw.attendance.today === undefined && detail.today ? { today: detail.today } : {}),
      ...(raw.attendance.last7Rate === undefined && detail.last7
        ? { last7Rate: detail.last7.rate }
        : {}),
      ...(raw.attendance.belowThreshold === undefined && detail.belowThreshold !== undefined
        ? { belowThreshold: detail.belowThreshold }
        : {}),
      series: raw.attendance.series?.length
        ? raw.attendance.series
        : (detail.series ?? []).map(({ day, rate }) => ({ day, rate })),
    };
  }

  // Academics: per-scheme donuts and the normalised deciles under their agreed
  // names; the at-risk count borrows the top-level attendance-risk figure when
  // the block itself carries none.
  const academics = raw.academics;
  if (academics) {
    out.academics = {
      ...academics,
      ...(academics.atRisk === undefined && raw.atRisk
        ? { atRisk: raw.atRisk.count }
        : {}),
      ...(academics.distributions === undefined && academics.byScheme
        ? {
            distributions: academics.byScheme.map((block) => ({
              schemeLabel: block.schemeName ?? block.schemeId ?? '',
              bands: block.distribution ?? [],
            })),
          }
        : {}),
      ...(academics.normalized === undefined && academics.deciles
        ? { normalized: academics.deciles }
        : {}),
    };
  }

  // Population: the residency split under its agreed name.
  const population = raw.population;
  if (population && population.boarding === undefined && population.residency) {
    out.population = {
      ...population,
      boarding: { boarders: population.residency.boarding, day: population.residency.day },
    };
  }

  // Admissions funnel: nested in the legacy block on the wire.
  if (raw.admissionsFunnel === undefined) {
    const funnel = raw.admissions?.funnel;
    if (funnel) {
      out.admissionsFunnel = {
        ...(funnel.cycle?.label ? { cycleLabel: funnel.cycle.label } : {}),
        applications: funnel.applications,
        underReview: funnel.underReview,
        assessed: funnel.assessed,
        offered: funnel.offered,
        accepted: funnel.accepted,
        enrolled: funnel.enrolled,
        ...(funnel.pendingOver7Days !== undefined
          ? { pendingOver7Days: funnel.pendingOver7Days }
          : {}),
        ...(funnel.placementScheduled !== undefined
          ? { placementScheduled: funnel.placementScheduled }
          : {}),
      };
    } else if (raw.capabilities?.admissions === false) {
      out.admissionsFunnel = null;
    }
  }

  // Availability: assembled from the per-block wire states when the server did
  // not send the map itself.
  if (raw.availability === undefined) {
    const availability: Partial<Record<OverviewDomain, OverviewAvailability>> = {};
    const attendance = availabilityOf(detail?.availability);
    if (attendance) availability.attendance = attendance;
    const academic = availabilityOf(academics?.availability);
    if (academic) availability.academics = academic;
    const money = availabilityOf(finance?.availability);
    if (money) availability.finance = money;
    if (Object.keys(availability).length > 0) out.availability = availability;
  }

  return out as AcademicOverviewInsights;
}
